import type { LandingDoc } from './SeoLandingPage'

export const LANDING_DOCS: Record<string, LandingDoc> = {
  '/gold-prediction-trading': {
    path: '/gold-prediction-trading',
    eyebrow: 'Markets',
    sections: [
      {
        heading: 'Gold on Perfect Predict',
        paragraphs: [
          'Gold (XAU/USD) is available as a Live prediction market on Perfect Predict. Structured prediction trading on Gold uses predefined strategy models rather than open-ended discretionary chart trading on the public website.',
        ],
      },
      {
        heading: 'How a Gold prediction works',
        paragraphs: [
          'You select a strategy profile — SAFE, BALANCE or AGGRESSIVE — then choose BUY or SELL. Perfect Predict does not choose market direction for you. The selected profile presents predefined target and stop parameters before you trade.',
        ],
        bullets: [
          'Instrument: Gold · XAU/USD',
          'Status: Live',
          'Strategies: SAFE, BALANCE, AGGRESSIVE',
          'Execution: MetaTrader 5 infrastructure',
        ],
      },
      {
        heading: 'Illustrative parameters, not guarantees',
        paragraphs: [
          'Strategy cards and payout examples on Perfect Predict are illustrative. They describe risk/reward profiles and example figures — they do not guarantee profit, fill quality, or any particular outcome. Trading involves the risk of loss.',
        ],
      },
      {
        heading: 'Account access',
        paragraphs: [
          'Day-to-day account management happens in the Perfect Predict Client Portal. Live accounts can be funded through the portal; Demo accounts receive a virtual opening balance and require no funding.',
        ],
      },
    ],
    related: [
      { label: 'Silver prediction trading', to: '/silver-prediction-trading' },
      { label: 'Prediction strategies', to: '/prediction-strategies' },
      { label: 'How Perfect Predict works', to: '/how-perfect-predict-works' },
      { label: 'Risk Disclosure', to: '/risk-disclosure' },
    ],
  },
  '/silver-prediction-trading': {
    path: '/silver-prediction-trading',
    eyebrow: 'Markets',
    sections: [
      {
        heading: 'Silver on Perfect Predict',
        paragraphs: [
          'Silver (XAG/USD) is available as a Live prediction market on Perfect Predict alongside Gold. Trading uses the same structured approach: predefined strategy profiles, trader-selected direction, and MetaTrader 5 execution.',
        ],
      },
      {
        heading: 'How a Silver prediction works',
        paragraphs: [
          'Choose SAFE, BALANCE or AGGRESSIVE, then select BUY or SELL. Target and stop parameters come from the selected strategy profile. Perfect Predict does not decide direction on your behalf.',
        ],
        bullets: [
          'Instrument: Silver · XAG/USD',
          'Status: Live',
          'Strategies: SAFE, BALANCE, AGGRESSIVE',
          'Execution: MetaTrader 5 infrastructure',
        ],
      },
      {
        heading: 'Risk and illustrations',
        paragraphs: [
          'Payout examples and strategy figures are illustrative only. Actual outcomes depend on market movement, execution conditions and the selected strategy. Review the Risk Disclosure before opening a Live account.',
        ],
      },
    ],
    related: [
      { label: 'Gold prediction trading', to: '/gold-prediction-trading' },
      { label: 'Prediction strategies', to: '/prediction-strategies' },
      { label: 'Funding and withdrawals', to: '/funding-and-withdrawals' },
      { label: 'Risk Disclosure', to: '/risk-disclosure' },
    ],
  },
  '/prediction-strategies': {
    path: '/prediction-strategies',
    eyebrow: 'Strategies',
    sections: [
      {
        heading: 'Strategy profiles, not promises',
        paragraphs: [
          'SAFE, BALANCE and AGGRESSIVE are predefined strategy/risk-reward profiles. The names describe structured parameter sets — they do not indicate certainty, lower loss probability, or guaranteed success.',
        ],
      },
      {
        heading: 'What each profile presents',
        paragraphs: [
          'Each profile presents illustrative target, stop and payout parameters so you can see the structure before trading. Homepage strategy cards label examples clearly as illustrative.',
        ],
        bullets: [
          'SAFE — lower intensity illustrative profile',
          'BALANCE — mid-range illustrative profile',
          'AGGRESSIVE — higher intensity illustrative profile',
        ],
      },
      {
        heading: 'You still choose direction',
        paragraphs: [
          'After selecting a strategy profile, you choose BUY or SELL. Perfect Predict does not select market direction for the trader. Execution then proceeds through MetaTrader 5 infrastructure.',
        ],
      },
      {
        heading: 'Available markets',
        paragraphs: [
          'These strategy profiles are available on Live Gold (XAU/USD) and Silver (XAG/USD) prediction markets.',
        ],
      },
    ],
    related: [
      { label: 'How Perfect Predict works', to: '/how-perfect-predict-works' },
      { label: 'Gold prediction trading', to: '/gold-prediction-trading' },
      { label: 'Silver prediction trading', to: '/silver-prediction-trading' },
      { label: 'Risk Disclosure', to: '/risk-disclosure' },
    ],
  },
  '/how-perfect-predict-works': {
    path: '/how-perfect-predict-works',
    eyebrow: 'Product',
    sections: [
      {
        heading: 'Four clear steps',
        paragraphs: [
          'Perfect Predict is designed around a structured prediction flow rather than an open-ended discretionary ticket on the public website.',
        ],
        bullets: [
          'Choose a strategy profile (SAFE, BALANCE or AGGRESSIVE)',
          'Choose direction (BUY or SELL) — you decide',
          'The trade executes through MetaTrader 5 infrastructure',
          'The selected strategy’s predefined target or stop determines the result',
        ],
      },
      {
        heading: 'Markets',
        paragraphs: [
          'Currently available Live markets are Gold (XAU/USD) and Silver (XAG/USD). Additional markets may be introduced over time.',
        ],
      },
      {
        heading: 'Where you manage the account',
        paragraphs: [
          'Registration, funding, withdrawals and support messaging happen in the Perfect Predict Client Portal. The public website explains the product; the portal is where accounts are operated.',
        ],
      },
    ],
    related: [
      { label: 'Prediction strategies', to: '/prediction-strategies' },
      { label: 'MT5 trading infrastructure', to: '/mt5-trading-infrastructure' },
      { label: 'Demo trading account', to: '/demo-trading-account' },
      { label: 'About Perfect Predict', to: '/about' },
    ],
  },
  '/mt5-trading-infrastructure': {
    path: '/mt5-trading-infrastructure',
    eyebrow: 'Platform',
    sections: [
      {
        heading: 'Client Portal plus MetaTrader 5',
        paragraphs: [
          'Perfect Predict separates day-to-day account management from market execution. You manage Live and Demo accounts in the Client Portal. Trade execution is handled through MetaTrader 5 infrastructure.',
        ],
      },
      {
        heading: 'What that means in practice',
        paragraphs: [
          'The public website describes structured prediction trading and links you into registration. After you have an account, portal tools cover funding, withdrawals, support and launching trading access. Execution connectivity uses MetaTrader 5 infrastructure.',
        ],
      },
      {
        heading: 'Security focus',
        paragraphs: [
          'Account credentials and client data are handled through the secure Client Portal and related service infrastructure — not embedded as live account data on the public marketing website.',
        ],
      },
    ],
    related: [
      { label: 'How Perfect Predict works', to: '/how-perfect-predict-works' },
      { label: 'Funding and withdrawals', to: '/funding-and-withdrawals' },
      { label: 'Support', to: '/support' },
      { label: 'Risk Disclosure', to: '/risk-disclosure' },
    ],
  },
  '/demo-trading-account': {
    path: '/demo-trading-account',
    eyebrow: 'Accounts',
    sections: [
      {
        heading: 'Practice without funding',
        paragraphs: [
          'A Perfect Predict Demo account includes a virtual opening balance. Demo accounts require no funding, so you can explore the structured prediction experience before opening a Live account.',
        ],
      },
      {
        heading: 'Demo vs Live',
        paragraphs: [
          'Live accounts are used for real funding and live trading access. Demo is for practice with virtual balance. Both are managed from the Client Portal account center.',
        ],
        bullets: [
          'Demo — virtual opening balance, no funding required',
          'Live — fund via supported Client Portal method to trade with real capital',
        ],
      },
      {
        heading: 'Getting started',
        paragraphs: [
          'Create an account through the Client Portal, then open a Demo account from the account center when available. When you are ready, open and fund a Live account subject to verification requirements.',
        ],
      },
    ],
    related: [
      { label: 'Funding and withdrawals', to: '/funding-and-withdrawals' },
      { label: 'How Perfect Predict works', to: '/how-perfect-predict-works' },
      { label: 'Support', to: '/support' },
      { label: 'Risk Disclosure', to: '/risk-disclosure' },
    ],
  },
  '/funding-and-withdrawals': {
    path: '/funding-and-withdrawals',
    eyebrow: 'Funding',
    sections: [
      {
        heading: 'Funding a Live account',
        paragraphs: [
          'Live accounts can be funded through the secure Client Portal. The currently described supported funding method on the public website is Uniwire. Demo accounts require no funding and receive a virtual opening balance.',
        ],
      },
      {
        heading: 'Typical Live funding path',
        paragraphs: ['The public website describes this high-level sequence:'],
        bullets: [
          'Create a Live account',
          'Complete verification if required',
          'Fund via the supported method (Uniwire)',
          'Funds credited — start trading',
        ],
      },
      {
        heading: 'Withdrawals',
        paragraphs: [
          'Withdrawals are managed through the Client Portal. Processing can depend on identity verification, supported payment providers, network conditions and compliance checks. Processing times can vary and are not guaranteed.',
        ],
      },
      {
        heading: 'Need help?',
        paragraphs: [
          'For funding or withdrawal questions tied to your identity, use Client Portal Support after signing in.',
        ],
      },
    ],
    related: [
      { label: 'Demo trading account', to: '/demo-trading-account' },
      { label: 'Support', to: '/support' },
      { label: 'MT5 trading infrastructure', to: '/mt5-trading-infrastructure' },
      { label: 'Risk Disclosure', to: '/risk-disclosure' },
    ],
  },
}
